import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import {
  FaUser, FaUserPlus, FaTrash, FaKey, FaSync, FaSpinner,
  FaCheck, FaTimes, FaShieldAlt, FaToggleOn, FaToggleOff,
  FaExclamationTriangle, FaInfoCircle,
  FaEye, FaEyeSlash, FaEdit
} from 'react-icons/fa';
import './RouterAccessManagement.scss';
import * as Sentry from '@sentry/react';

// ── Internal helper: wraps apiFetch with automatic JSON parsing ──────────────
const useApiCall = () => {
  const { apiFetch } = useAuth();
  return useCallback(async (url, opts = {}) => {
    const res = await apiFetch(url, opts);
    const json = await res.json();
    if (!json.success) throw new Error(json.error || 'Request failed');
    return json;
  }, [apiFetch]);
};

// ── Known RouterOS IP services with defaults for display purposes ────────────
const SERVICE_META = {
  ftp:     { label: 'FTP',          defaultPort: 21,   risk: 'high',   desc: 'File Transfer Protocol — insecure, consider disabling.' },
  telnet:  { label: 'Telnet',       defaultPort: 23,   risk: 'high',   desc: 'Unencrypted remote shell — disable in favour of SSH.' },
  www:     { label: 'HTTP (Web)',    defaultPort: 80,   risk: 'medium', desc: 'Web-based configuration — unencrypted.' },
  ssh:     { label: 'SSH',          defaultPort: 22,   risk: 'low',    desc: 'Encrypted remote shell — recommended for remote access.' },
  'www-ssl': { label: 'HTTPS (Web)', defaultPort: 443, risk: 'low',    desc: 'Encrypted web-based configuration.' },
  'api':   { label: 'API',          defaultPort: 8728, risk: 'medium', desc: 'RouterOS API — used by CapTrac for management.' },
  'api-ssl': { label: 'API (SSL)',  defaultPort: 8729, risk: 'low',    desc: 'Encrypted RouterOS API — recommended over plain API.' },
  winbox:  { label: 'Winbox',       defaultPort: 8291, risk: 'low',    desc: 'MikroTik Winbox GUI client.' },
};

const riskColour = (risk) => risk === 'high' ? '#d32f2f' : risk === 'medium' ? '#ffa000' : '#2e7d32';

// ═══════════════════════════════════════════════════════════════════════════════
// RouterAccessManagement
// Provides two sub-sections:
//   1. System Users — view, add, delete and change passwords for RouterOS users.
//   2. IP Services  — enable/disable services and restrict allowed addresses.
// All write operations are audit-logged via the backend.
// ═══════════════════════════════════════════════════════════════════════════════
const RouterAccessManagement = ({ routerId }) => {
  const [subTab, setSubTab] = useState('users');

  return (
    <div className="router-access-management">
      {/* Sub-navigation */}
      <div className="access-subtabs">
        {[
          { key: 'users',    label: 'Users',       icon: <FaUser /> },
          { key: 'services', label: 'IP Services', icon: <FaShieldAlt /> },
        ].map(t => (
          <button
            key={t.key}
            className={`access-subtab${subTab === t.key ? ' active' : ''}`}
            onClick={() => setSubTab(t.key)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {subTab === 'users'    && <UsersPanel    routerId={routerId} />}
      {subTab === 'services' && <ServicesPanel routerId={routerId} />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// UsersPanel — list, create, delete RouterOS local users
// ═══════════════════════════════════════════════════════════════════════════════
const UsersPanel = ({ routerId }) => {
  const apiCall = useApiCall();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingAction, setSavingAction] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', group: 'read', password: '', comment: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [changePasswordUser, setChangePasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const mounted = useRef(false);

  const load = useCallback(async () => {
    if (!routerId) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiCall(`/api/RouterConfig/${routerId}/system-users`);
      setUsers(data.data || []);
    } catch (e) {
        Sentry.captureException(e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [routerId, apiCall]);

  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(); }
  }, [load]);

  const handleAddUser = async () => {
    if (!newUser.name.trim()) { setError('Username is required.'); return; }
    setSavingAction('add');
    setError('');
    try {
      await apiCall(`/api/RouterConfig/${routerId}/system-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser)
      });
      setSuccess(`User "${newUser.name}" created successfully.`);
      setShowAddForm(false);
      setNewUser({ name: '', group: 'read', password: '', comment: '' });
      load();
    } catch (e) {
        Sentry.captureException(e);
      setError(e.message);
    } finally {
      setSavingAction('');
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Delete user "${username}" from this router?`)) return;
    setSavingAction(`delete-${username}`);
    setError('');
    try {
      await apiCall(`/api/RouterConfig/${routerId}/system-users/${encodeURIComponent(username)}`, {
        method: 'DELETE'
      });
      setSuccess(`User "${username}" deleted.`);
      load();
    } catch (e) {
        Sentry.captureException(e);
      setError(e.message);
    } finally {
      setSavingAction('');
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword.trim()) { setError('New password cannot be empty.'); return; }
    setSavingAction(`password-${changePasswordUser}`);
    setError('');
    try {
      await apiCall(
        `/api/RouterConfig/${routerId}/system-users/${encodeURIComponent(changePasswordUser)}/password`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ newPassword })
        }
      );
      setSuccess(`Password changed for "${changePasswordUser}".`);
      setChangePasswordUser(null);
      setNewPassword('');
    } catch (e) {
        Sentry.captureException(e);
      setError(e.message);
    } finally {
      setSavingAction('');
    }
  };

  return (
    <div className="panel access-panel">
      {/* Toolbar */}
      <div className="panel-toolbar">
        <h3 className="access-section-title"><FaUser /> System Users</h3>
        <div className="toolbar-actions">
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAddForm(v => !v)}>
            <FaUserPlus /> Add User
          </button>
        </div>
      </div>

      {/* Info callout */}
      <div className="access-info">
        <FaInfoCircle /> Manage local RouterOS user accounts. All changes are audit-logged.
        Do not remove the last full-access user.
      </div>

      {/* Feedback messages */}
      {error   && <div className="access-alert access-alert--error"><FaExclamationTriangle /> {error}</div>}
      {success && <div className="access-alert access-alert--success"><FaCheck /> {success}</div>}

      {/* Add User Form */}
      {showAddForm && (
        <div className="access-form">
          <div className="access-form-grid">
            <div className="form-group">
              <label>Username</label>
              <input
                placeholder="e.g. helpdesk"
                value={newUser.name}
                onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Group</label>
              <select value={newUser.group} onChange={e => setNewUser(p => ({ ...p, group: e.target.value }))}>
                <option value="full">full (admin)</option>
                <option value="write">write</option>
                <option value="read">read</option>
              </select>
            </div>
            <div className="form-group">
              <label>Password</label>
              <div className="password-input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Initial password"
                  value={newUser.password}
                  onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))}
                />
                <button className="btn-icon password-toggle" onClick={() => setShowPassword(v => !v)}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label>Comment <span className="optional">(optional)</span></label>
              <input
                placeholder="e.g. Help desk read-only"
                value={newUser.comment}
                onChange={e => setNewUser(p => ({ ...p, comment: e.target.value }))}
              />
            </div>
          </div>
          <div className="access-form-actions">
            <button className="btn btn-primary btn-sm" onClick={handleAddUser} disabled={savingAction === 'add'}>
              {savingAction === 'add' ? <FaSpinner className="spin" /> : <FaCheck />} Create User
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => setShowAddForm(false)}>
              <FaTimes /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Change Password Inline Form */}
      {changePasswordUser && (
        <div className="access-form">
          <strong><FaKey /> Change password for <em>{changePasswordUser}</em></strong>
          <div className="access-form-grid" style={{ marginTop: 10 }}>
            <div className="form-group">
              <label>New Password</label>
              <div className="password-input-wrap">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="New password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                />
                <button className="btn-icon password-toggle" onClick={() => setShowNewPassword(v => !v)}>
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
          </div>
          <div className="access-form-actions">
            <button
              className="btn btn-primary btn-sm"
              onClick={handleChangePassword}
              disabled={savingAction.startsWith('password-')}
            >
              {savingAction.startsWith('password-') ? <FaSpinner className="spin" /> : <FaKey />} Set Password
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => { setChangePasswordUser(null); setNewPassword(''); }}>
              <FaTimes /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      {loading && users.length === 0 ? (
        <div className="empty">Loading users…</div>
      ) : users.length === 0 ? (
        <div className="empty">No users found on this router.</div>
      ) : (
        <table className="data-table access-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Group</th>
              <th>Comment</th>
              <th>Last Login</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id || u.name} className={u.disabled ? 'row-disabled' : ''}>
                <td style={{ fontWeight: 600 }}>
                  <FaUser style={{ marginRight: 6, opacity: 0.5 }} />
                  {u.name}
                  {u.disabled && <span className="badge badge-type" style={{ marginLeft: 6 }}>disabled</span>}
                </td>
                <td>
                  <span className={`badge ${u.group === 'full' ? 'badge-danger' : u.group === 'write' ? 'badge-type' : 'badge-success'}`}>
                    {u.group}
                  </span>
                </td>
                <td className="muted">{u.comment || '—'}</td>
                <td className="mono muted" style={{ fontSize: 11 }}>{u.lastLoggedIn || '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="btn btn-outline btn-xs"
                    title="Change password"
                    onClick={() => { setChangePasswordUser(u.name); setNewPassword(''); }}
                    disabled={!!savingAction}
                  >
                    <FaKey />
                  </button>
                  {u.name !== 'admin' && (
                    <button
                      className="btn btn-danger btn-xs"
                      title="Delete user"
                      onClick={() => handleDeleteUser(u.name)}
                      disabled={!!savingAction}
                      style={{ marginLeft: 6 }}
                    >
                      {savingAction === `delete-${u.name}` ? <FaSpinner className="spin" /> : <FaTrash />}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ServicesPanel — enable/disable IP services and manage ports / allowed addresses
// ═══════════════════════════════════════════════════════════════════════════════
const ServicesPanel = ({ routerId }) => {
  const apiCall = useApiCall();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingAction, setSavingAction] = useState('');
  const [editingService, setEditingService] = useState(null);
  const [editForm, setEditForm] = useState({ port: '', address: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const mounted = useRef(false);

  const load = useCallback(async () => {
    if (!routerId) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiCall(`/api/RouterConfig/${routerId}/ip-services`);
      setServices(data.data || []);
    } catch (e) {
        Sentry.captureException(e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [routerId, apiCall]);

  useEffect(() => {
    if (!mounted.current) { mounted.current = true; load(); }
  }, [load]);

  const handleToggle = async (service) => {
    setSavingAction(`toggle-${service.name}`);
    setError('');
    try {
      await apiCall(`/api/RouterConfig/${routerId}/ip-services/${encodeURIComponent(service.name)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ disabled: !service.disabled })
      });
      setSuccess(`${service.name} ${service.disabled ? 'enabled' : 'disabled'}.`);
      load();
    } catch (e) {
        Sentry.captureException(e);
      setError(e.message);
    } finally {
      setSavingAction('');
    }
  };

  const handleEditSave = async () => {
    if (!editingService) return;
    setSavingAction(`edit-${editingService}`);
    setError('');
    try {
      const payload = {};
      if (editForm.port)    payload.port    = editForm.port;
      if (editForm.address !== undefined) payload.address = editForm.address;
      await apiCall(`/api/RouterConfig/${routerId}/ip-services/${encodeURIComponent(editingService)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      setSuccess(`${editingService} updated.`);
      setEditingService(null);
      load();
    } catch (e) {
        Sentry.captureException(e);
      setError(e.message);
    } finally {
      setSavingAction('');
    }
  };

  return (
    <div className="panel access-panel">
      {/* Toolbar */}
      <div className="panel-toolbar">
        <h3 className="access-section-title"><FaShieldAlt /> IP Services</h3>
        <div className="toolbar-actions">
          <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            {loading ? <FaSpinner className="spin" /> : <FaSync />} Refresh
          </button>
        </div>
      </div>

      <div className="access-info">
        <FaInfoCircle /> Enable or disable RouterOS IP services and restrict which source addresses
        may connect. Disabling unused services reduces the router's attack surface.
      </div>

      {error   && <div className="access-alert access-alert--error"><FaExclamationTriangle /> {error}</div>}
      {success && <div className="access-alert access-alert--success"><FaCheck /> {success}</div>}

      {loading && services.length === 0 ? (
        <div className="empty">Loading services…</div>
      ) : services.length === 0 ? (
        <div className="empty">No services returned by this router.</div>
      ) : (
        <table className="data-table access-table">
          <thead>
            <tr>
              <th>Service</th>
              <th style={{ textAlign: 'center' }}>Status</th>
              <th>Port</th>
              <th>Allowed Addresses</th>
              <th>Risk</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.map(svc => {
              const meta = SERVICE_META[svc.name] || {};
              const isEditing = editingService === svc.name;
              return (
                <>
                  <tr key={svc.name} className={svc.disabled ? 'row-disabled' : ''}>
                    <td style={{ fontWeight: 600 }}>
                      {meta.label || svc.name}
                      {meta.desc && (
                        <div style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>{meta.desc}</div>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {svc.disabled
                        ? <span className="badge badge-type">Disabled</span>
                        : <span className="badge badge-success">Enabled</span>}
                    </td>
                    <td className="mono">{svc.port || meta.defaultPort || '—'}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{svc.address || 'any'}</td>
                    <td>
                      {meta.risk ? (
                        <span style={{ color: riskColour(meta.risk), fontWeight: 600, fontSize: 12 }}>
                          {meta.risk.toUpperCase()}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        className="btn btn-outline btn-xs"
                        title="Edit port / allowed addresses"
                        onClick={() => {
                          setEditingService(isEditing ? null : svc.name);
                          setEditForm({ port: svc.port || '', address: svc.address || '' });
                        }}
                        disabled={!!savingAction}
                        style={{ marginRight: 6 }}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn btn-outline btn-xs"
                        title={svc.disabled ? 'Enable service' : 'Disable service'}
                        onClick={() => handleToggle(svc)}
                        disabled={!!savingAction}
                      >
                        {savingAction === `toggle-${svc.name}`
                          ? <FaSpinner className="spin" />
                          : svc.disabled ? <FaToggleOff className="toggle-off" /> : <FaToggleOn className="toggle-on" />}
                      </button>
                    </td>
                  </tr>

                  {/* Inline edit row */}
                  {isEditing && (
                    <tr key={`${svc.name}-edit`}>
                      <td colSpan={6}>
                        <div className="access-form" style={{ margin: '4px 0 8px 0' }}>
                          <div className="access-form-grid">
                            <div className="form-group">
                              <label>Port</label>
                              <input
                                type="number"
                                min="1"
                                max="65535"
                                value={editForm.port}
                                onChange={e => setEditForm(p => ({ ...p, port: e.target.value }))}
                                placeholder={String(meta.defaultPort || '')}
                              />
                            </div>
                            <div className="form-group">
                              <label>Allowed Addresses <span className="optional">(CIDR, blank = any)</span></label>
                              <input
                                value={editForm.address}
                                onChange={e => setEditForm(p => ({ ...p, address: e.target.value }))}
                                placeholder="e.g. 10.0.0.0/8 or leave blank for any"
                              />
                            </div>
                          </div>
                          <div className="access-form-actions">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={handleEditSave}
                              disabled={savingAction.startsWith('edit-')}
                            >
                              {savingAction.startsWith('edit-') ? <FaSpinner className="spin" /> : <FaCheck />} Save
                            </button>
                            <button className="btn btn-outline btn-sm" onClick={() => setEditingService(null)}>
                              <FaTimes /> Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RouterAccessManagement;
